// src/pages/Maintainers/Crops/Crops.jsx
import { useMemo, useState, useCallback, useEffect } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";

import styles from "./crops.module.scss";
import { useCrops } from "@/hooks/maintainers/useCrops";
import { FALLBACK_PARENT, groupCrops } from "./groupCrops";

const Crops = () => {
	const { data, isLoading, isFetching } = useCrops({});

	const items = useMemo(() => {
		if (Array.isArray(data?.crops)) return data.crops;
		if (Array.isArray(data)) return data;
		return [];
	}, [data]);

	const grouped = useMemo(() => groupCrops(items), [items]);

	const [expanded, setExpanded] = useState(() => new Set());
	useEffect(() => {
		const hasFallback = grouped.some(([p]) => p === FALLBACK_PARENT);
		if (hasFallback) {
			setExpanded((prev) => (prev.has(FALLBACK_PARENT) ? prev : new Set([...prev, FALLBACK_PARENT])));
		}
	}, [grouped]);

	const toggleGroup = useCallback((key) => {
		setExpanded((prev) => {
			const next = new Set(prev);
			next.has(key) ? next.delete(key) : next.add(key);
			return next;
		});
	}, []);

	const expandAll = useCallback(() => setExpanded(new Set(grouped.map(([p]) => p))), [grouped]);
	const collapseAll = useCallback(() => setExpanded(new Set()), []);

	if (isLoading) {
		return (
			<div className={styles.container}>
				<header className={styles.header}>
					<h1 className={styles.title}>SAG Especies</h1>
				</header>
				<div className={styles.skeleton}>Cargando…</div>
			</div>
		);
	}

	return (
		<div className={styles.container}>
			<header className={styles.header}>
				<h1 className={styles.title}>SAG Especies</h1>
				{isFetching && <span className={styles.badge}>Actualizando…</span>}
			</header>

			<div className={styles.toolbar}>
				<button
					type="button"
					className={styles.btn}
					onClick={() => (expanded.size === grouped.length ? collapseAll() : expandAll())}
					disabled={!grouped.length}
				>
					{expanded.size === grouped.length ? (
						<>
							<ChevronUp size={16} style={{ verticalAlign: "middle", marginRight: 4 }} />
							Contraer todo
						</>
					) : (
						<>
							<ChevronDown size={16} style={{ verticalAlign: "middle", marginRight: 4 }} />
							Expandir todo ({grouped.length})
						</>
					)}
				</button>
			</div>

			<section className={styles.groups}>
				{grouped.map(([parent, list]) => {
					const isOpen = expanded.has(parent);
					return (
						<article key={parent} className={styles.group}>
							<button type="button" className={styles.groupHeader} onClick={() => toggleGroup(parent)} aria-expanded={isOpen}>
								<span className={styles.groupTitle}>{parent.toUpperCase()}</span>
								<span className={styles.count}>{list.length}</span>
								<span className={styles.chevron} aria-hidden>
									{isOpen ? "▾" : "▸"}
								</span>
							</button>

							{isOpen && (
								<ul className={styles.list}>
									{list.map((it) => (
										<li key={it.id} className={styles.item}>
											<span className={styles.itemName}>{it.name}</span>
										</li>
									))}
								</ul>
							)}
						</article>
					);
				})}
			</section>
		</div>
	);
};

export default Crops;
